/**
 * The review-queue's per-draft warnings, fetched lazily for the rows on screen.
 *
 * The guides list returns rows without a body (the list query selects no contentJson/contentHtml),
 * so the same `bodyWarnings` the editor runs — under 300 words, fewer than five internal links,
 * button placeholders, unlinked guide titles, a body image with no banner — cannot be computed from
 * a row alone. When the review view is on, this reads each visible draft's body once through
 * GET /api/guides/:id and the published titles once through /api/guides/titles, caches both, and
 * returns a map the warnings cell draws from. It fetches a few at a time so a page of drafts does
 * not open fifty requests.
 *
 * Kept out of the list component so Phase 2d is a hook and a cell wired in, not a rebuild of a
 * shared file every sibling branch also touches.
 */
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';

import { type BodyWarning, type GuideTitle, bodyWarnings } from './GuideEditor/guideWarnings';
import { parseGuide, readingStats } from './GuideEditor/sections';

export interface DraftWarningState {
  loading: boolean;
  warnings: BodyWarning[];
  /** Set when the body could not be read; the cell says so rather than claiming "ready". */
  error?: boolean;
}

/** Only drafts are reviewed; an import stub or a published guide is not in this queue. */
const isReviewableDraft = (status?: string) => (status || '').toUpperCase() === 'DRAFT';

/** How many draft bodies to read at once. */
const CONCURRENCY = 4;

interface MinimalRow {
  _id: string;
  status?: string;
}

interface GuideBody {
  slug?: string;
  contentHtml?: string;
  contentJson?: string;
}

/** The soft checks for one fetched guide body. Exported so the wiring can be tested directly. */
export function warningsForBody(body: GuideBody, titles: readonly GuideTitle[]): BodyWarning[] {
  const contentJson = body.contentJson ?? '';
  const contentHtml = body.contentHtml ?? '';
  const parsed = parseGuide(contentJson);
  const jsonWords = parsed.ok ? readingStats(parsed.obj).words : 0;
  return bodyWarnings({
    contentHtml,
    contentJson,
    jsonWords,
    guideTitles: titles,
    slug: body.slug ?? '',
  });
}

/**
 * Warnings for the draft rows currently on screen, keyed by guide id.
 *
 * Returns an empty map when the review view is off, so the list pays nothing for a column it is not
 * showing. A row that is still loading, or whose body failed to load, carries that in its entry so
 * the cell can show a spinner or a dash rather than a misleading "ready".
 */
export function useDraftWarnings(
  rows: MinimalRow[],
  active: boolean,
): Record<string, DraftWarningState> {
  const [state, setState] = useState<Record<string, DraftWarningState>>({});
  const titlesRef = useRef<GuideTitle[] | null>(null);
  // Bodies never change under us within a view, so a fetched one is cached across re-renders and
  // page changes rather than re-read every time the rows array is rebuilt.
  const cacheRef = useRef<Record<string, DraftWarningState>>({});

  useEffect(() => {
    if (!active) {
      setState({});
      return undefined;
    }
    let cancelled = false;

    const run = async () => {
      let titles = titlesRef.current;
      if (!titles) {
        try {
          const { data } = await axios.get('/api/guides/titles');
          titles = Array.isArray(data) ? (data as GuideTitle[]) : [];
        } catch {
          titles = [];
        }
        titlesRef.current = titles;
      }

      const todo = rows
        .filter((r) => isReviewableDraft(r.status) && !cacheRef.current[r._id])
        .map((r) => r._id);

      // Show what is cached immediately, and a spinner for the rest.
      if (!cancelled) {
        const seed: Record<string, DraftWarningState> = {};
        for (const r of rows) {
          if (!isReviewableDraft(r.status)) continue;
          seed[r._id] = cacheRef.current[r._id] ?? { loading: true, warnings: [] };
        }
        setState(seed);
      }

      let cursor = 0;
      const worker = async () => {
        while (cursor < todo.length) {
          const id = todo[cursor++];
          let entry: DraftWarningState;
          try {
            const { data } = await axios.get(`/api/guides/${id}`);
            entry = { loading: false, warnings: warningsForBody(data ?? {}, titles ?? []) };
          } catch {
            entry = { loading: false, warnings: [], error: true };
          }
          cacheRef.current[id] = entry;
          if (!cancelled) setState((prev) => ({ ...prev, [id]: entry }));
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, todo.length) }, worker));
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [rows, active]);

  return state;
}
